import { Calendar } from "lucide-react"
import StyledSelectDropdown from "../StyledSelectDropdown"
import { FormField, FORM_INPUT_CLASS, FORM_LABEL_CLASS, SectionHeader } from "./CreateJobFields"

type GeneralInformationSectionProps = {
    formData: {
        openingDate: string
        closingDate: string
        jobTitle: string
    }
    isEditMode: boolean
    jobLevels: any[]
    jobLevelsStatus: string
    selectedJobLevelId: number | null
    regionsFiltered: any[]
    regionsStatus: string
    selectedRegionId: number | null
    countries: any[]
    countriesStatus: string
    selectedCountryId: number | null
    cities: any[]
    citiesStatus: string
    selectedCityId: number | null
    employmentTypes: any[]
    employmentTypesStatus: string
    selectedEmploymentTypeId: string | null
    workplaceTypes: any[]
    workplaceTypesStatus: string
    selectedWorkplaceTypeId: string | null
    nationalityCountries: any[]
    nationalityCountriesStatus: string
    selectedNationalityId: number | null
    onOpenCalendar: (which: "opening" | "closing") => void
    onJobTitleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onJobLevelChange: (value: string) => void
    onRegionChange: (value: string) => void
    onCountryChange: (value: string) => void
    onCityChange: (value: string) => void
    onEmploymentTypeChange: (value: string) => void
    onWorkplaceTypeChange: (value: string) => void
    onNationalityChange: (value: string) => void
    calendarNode: React.ReactNode
}

export const GeneralInformationSection = ({
    formData,
    isEditMode,
    jobLevels,
    jobLevelsStatus,
    selectedJobLevelId,
    regionsFiltered,
    regionsStatus,
    selectedRegionId,
    countries,
    countriesStatus,
    selectedCountryId,
    cities,
    citiesStatus,
    selectedCityId,
    employmentTypes,
    employmentTypesStatus,
    selectedEmploymentTypeId,
    workplaceTypes,
    workplaceTypesStatus,
    selectedWorkplaceTypeId,
    nationalityCountries,
    nationalityCountriesStatus,
    selectedNationalityId,
    onOpenCalendar,
    onJobTitleChange,
    onJobLevelChange,
    onRegionChange,
    onCountryChange,
    onCityChange,
    onEmploymentTypeChange,
    onWorkplaceTypeChange,
    onNationalityChange,
    calendarNode,
}: GeneralInformationSectionProps) => (
    <section className="mb-10">
        <SectionHeader title="General information" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div className="md:col-span-2 w-2/3">
                <FormField label="Job completion period" optional>
                    <div className="flex gap-4">
                        <div className="relative flex-1">
                            <label className={`${FORM_LABEL_CLASS} mb-1 block`}>
                                Opening Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Opening Date"
                                className={`w-full bg-[#F9F9F9] p-3 pr-10 rounded-md border-none ${FORM_INPUT_CLASS} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.openingDate ? "text-gray-800 font-semibold" : "text-gray-400"}`}
                                value={formData.openingDate}
                                readOnly
                            />
                            <button
                                type="button"
                                onClick={() => onOpenCalendar("opening")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
                            >
                                <Calendar className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="relative flex-1">
                            <label className={`${FORM_LABEL_CLASS} mb-1 block`}>
                                Closing Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Closing Date"
                                className={`w-full bg-[#F9F9F9] p-3 pr-10 rounded-md border-none ${FORM_INPUT_CLASS} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.closingDate ? "text-gray-800 font-semibold" : "text-gray-400"}`}
                                value={formData.closingDate}
                                readOnly
                            />
                            <button
                                type="button"
                                onClick={() => onOpenCalendar("closing")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
                            >
                                <Calendar className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </FormField>
            </div>

            {calendarNode}

            <FormField label="Job title" required>
                <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={onJobTitleChange}
                    placeholder="Enter job title"
                    className={`w-full bg-[#F9F9F9] p-4 rounded-md border-none ${FORM_INPUT_CLASS} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.jobTitle ? "text-gray-800 font-semibold" : "text-gray-400"}`}
                    autoComplete="off"
                    disabled={isEditMode}
                    readOnly={isEditMode}
                />
            </FormField>

            <FormField label="Job level" required>
                <StyledSelectDropdown
                    options={jobLevels}
                    value={selectedJobLevelId || ""}
                    onChange={onJobLevelChange}
                    placeholder="Select job level"
                    disabled={jobLevelsStatus === "loading" || isEditMode}
                />
            </FormField>

            <FormField label="Region" required>
                <StyledSelectDropdown
                    options={regionsFiltered}
                    value={selectedRegionId || ""}
                    onChange={onRegionChange}
                    placeholder="Select region"
                    disabled={regionsStatus === "loading" || isEditMode}
                />
            </FormField>

            <FormField label="Country" required>
                <StyledSelectDropdown
                    options={countries}
                    value={selectedCountryId ? String(selectedCountryId) : ""}
                    onChange={onCountryChange}
                    placeholder="Select country"
                    disabled={isEditMode || (!selectedRegionId || countriesStatus === "loading")}
                    disabledPlaceholder={
                        selectedCountryId && countries.length > 0
                            ? undefined
                            : !selectedRegionId
                                ? "Select region first"
                                : countriesStatus === "loading"
                                    ? "Loading..."
                                    : "Select country"
                    }
                />
            </FormField>

            <FormField label="City" required>
                <StyledSelectDropdown
                    options={cities}
                    value={selectedCityId ? String(selectedCityId) : ""}
                    onChange={onCityChange}
                    placeholder="Select city"
                    disabled={isEditMode || (!selectedCountryId || citiesStatus === "loading")}
                    disabledPlaceholder={
                        selectedCityId && cities.length > 0
                            ? undefined
                            : !selectedCountryId
                                ? "Select country first"
                                : citiesStatus === "loading"
                                    ? "Loading..."
                                    : "Select city"
                    }
                />
            </FormField>

            <FormField label="Employment type" required>
                <StyledSelectDropdown
                    options={employmentTypes}
                    value={selectedEmploymentTypeId || ""}
                    onChange={onEmploymentTypeChange}
                    placeholder="Select employment type"
                    disabled={employmentTypesStatus === "loading" || isEditMode}
                />
            </FormField>

            <FormField label="Workplace type" required>
                <StyledSelectDropdown
                    options={workplaceTypes}
                    value={selectedWorkplaceTypeId || ""}
                    onChange={onWorkplaceTypeChange}
                    placeholder="Select workplace type"
                    disabled={workplaceTypesStatus === "loading" || isEditMode}
                />
            </FormField>

            <FormField label="Nationality">
                <StyledSelectDropdown
                    options={nationalityCountries}
                    value={selectedNationalityId ? String(selectedNationalityId) : ""}
                    onChange={onNationalityChange}
                    placeholder="Select nationality"
                    disabled={isEditMode || nationalityCountriesStatus === "loading"}
                    disabledPlaceholder={
                        nationalityCountriesStatus === "loading"
                            ? "Loading..."
                            : undefined
                    }
                />
            </FormField>
        </div>
    </section>
)
